import { useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import Page from "~Common/Page";
import LoaderContainer from "~Common/Page/LoaderContainer";
import { SelectSeasonEntity } from "~components/SelectSeasonEntity/SelectSeasonEntity";
import { ImageTypes } from "~enums/imageTypes";
import { RosterStatuses } from "~enums/rosterStatuses";
import { useFetchSeason } from "~hooks/apiHooks/useFetchSeason.ts";
import { useHandlePagination } from "~hooks/useHandlePagination";
import { useNotification } from "~hooks/useNotification";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import RosterTable from "~pages/Roster/components/RosterTable/RosterTable";
import useDeleteRosterPlayer from "~pages/Roster/hooks/useDeleteRosterPlayer";
import useFetchEntity from "~pages/Roster/hooks/useFetchEntity";
import { useFetchInternationalGamesAmount } from "~pages/Roster/hooks/useFetchInternationalGamesAmount.ts";
import useFetchRoster from "~pages/Roster/hooks/useFetchRoster";
import { useGetPersonRosterInfo } from "~pages/Roster/hooks/useGetPersonRosterInfo";
import useUpsertRosterPlayer from "~pages/Roster/hooks/useUpsertRosterPlayer";
import type { UpsertSeasonRosterModel } from "~pages/Roster/types/upsertSeasonRosterModel";
import type { SeasonResponse } from "~types/season.ts";
import { findImageByType } from "~utilities/findImageByType";

const SeasonRosterPage = () => {
  const { uiConfiguration } = useSportConfiguration();
  const { entityId: entityIdParam, customerId, sport, seasonId, organizationId } = useParams();

  const [entityId, setEntityId] = useState(entityIdParam);

  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination(20);

  const params: Record<string, string> = {
    include: ["entities", "seasons", "persons"].join(","),
    limit: "1000",
    offset: "0",
  };

  const { data: entity, isLoading: isEntityLoading } = useFetchEntity(entityId);
  const { data: roster, isLoading: isRosterLoading } = useFetchRoster({
    customParams: params,
    entityId,
  });

  const { data: season, isLoading: isSeasonLoading } = useFetchSeason(seasonId, {}, (seasonData: SeasonResponse) => {
    return {
      discipline: seasonData.data[0]?.discipline,
    };
  });

  const { data: representing, isLoading: isIntGamesAmountPending } = useFetchInternationalGamesAmount({
    representing: entity?.representing,
    seasonId: seasonId,
    discipline: season?.discipline,
  });

  const { formatMessage } = useIntl();

  const { mutateAsync: upsertSeasonEntityRoster } = useUpsertRosterPlayer();
  const { mutateAsync: deleteSeasonRosterPlayer, isPending: isRosterDeleting } = useDeleteRosterPlayer();

  const { mutateAsync: isPersonInRoaster } = useGetPersonRosterInfo();
  const notification = useNotification();
  const handleUpsert = async (value: UpsertSeasonRosterModel) => {
    if (entityId && seasonId) {
      const { isPersonInRoster, roster } = await isPersonInRoaster({
        personId: value.personId,
        seasonId: seasonId,
      });
      if (!isPersonInRoster) {
        await upsertSeasonEntityRoster({
          status: RosterStatuses.Active,
          ...value,
          entityId,
          seasonId,
        });
      } else if (roster?.entityId === entityId) {
        await upsertSeasonEntityRoster({
          status: roster.status,
          ...value,
          entityId: roster?.entityId,
          seasonId,
        });
      } else {
        notification.error({
          message: formatMessage({
            id: "roster.error.person.exist.in.another.roster",
          }),
          placement: "bottomRight",
        });
      }
    } else {
      throw Error("Please specify entityId and seasonID");
    }
  };

  const handleDelete = async (value: string) => {
    await deleteSeasonRosterPlayer(value);
  };

  const rosterTableData = roster?.map((item) => ({
    ...item,
    nameFullLocal: item.person.nameFullLocal,
    avatarUrl: findImageByType(item.person?.images, ImageTypes.PersonHead),
    personId: item.personId,
    dob: item.person.dob,
    status: item.status,
    position: item.position,
    isCaptain: item.isCaptain,
    captainRoleId: item.captainRoleId,
    bib: item.bib,
    caps: representing ? representing[item.personId] : "",
  }));

  const isLoading = isEntityLoading || isRosterLoading || isRosterDeleting || isSeasonLoading || isIntGamesAmountPending;
  const navigate = useNavigate();

  return (
    <LoaderContainer loading={isEntityLoading}>
      <Page
        title={`${entity?.nameFullLocal || entity?.entityId} ${formatMessage({ id: "roster" })}`}
        onBack={() => {
          navigate(`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/entities`);
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "end",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <SelectSeasonEntity
            allowClear={false}
            style={{ width: 200 }}
            disabled={isLoading}
            onChange={(value) => {
              if (value?.value) {
                setEntityId(value?.value);
                navigate(`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/entities/${value?.value}/roster`);
              }
            }}
            value={{
              label: entity?.nameFullLocal || "",
              value: entityId || "",
            }}
          />
          <RosterTable
            isRosterEditable={true}
            entityId={entityId}
            handleDelete={handleDelete}
            handleUpsert={handleUpsert}
            queryKey={"seasonRoster"}
            isLoading={isLoading}
            data={rosterTableData || []}
            pagination={{
              currentPage,
              pageSize,
              pageSizeOptions,
              handlePaginationChange,
              offset,
            }}
            columnSettingsLocalStorageKey={`${sport}.tables.settings.columns.season.roster`}
            columnSortLocalStorageKey={`${sport}.tables.settings.sort.season.roster`}
            tableConfiguration={uiConfiguration.pages.seasonRoster.tableColumnGroups}
          />
        </div>
      </Page>
    </LoaderContainer>
  );
};

export default SeasonRosterPage;
